import React, { useEffect, useCallback, useState } from 'react';
import { AutoResizeTextarea } from '../shared/AutoResizeTextarea';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Button,
  HStack,
  VStack,
  Box,
  Text,
  Select,
  Spinner,
  Alert,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Collapse,
  Badge,
  Divider,
  IconButton,
  Tooltip,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { CopyIcon, CheckIcon, DownloadIcon, ArrowBackIcon, TimeIcon } from '@chakra-ui/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { XactoMatrixData, XactoGroupBy } from '../../hooks/useXactoMatrix';
import { useAiAnalysis, type ChatMessage, type HistoryConversation, type HistoryConversationDetail } from '../../hooks/useAiAnalysis';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AiAnalysisPanelProps {
  isOpen: boolean;
  onClose: () => void;
  matrixData: XactoMatrixData;
  groupBy: XactoGroupBy;
  period?: { start: string; end: string };
  viewMode?: 'pivot' | 'table' | 'flow' | 'chart' | 'payments';
  grouping?: 'merchant-month' | 'merchant-year' | 'category-month' | 'category-year';
}

// ─── CopyButton ───────────────────────────────────────────────────────────────

function CopyButton({
  getText,
  label = 'Copy',
  size = 'xs',
  variant = 'ghost',
}: {
  getText: () => string;
  label?: string;
  size?: string;
  variant?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(getText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard permission denied — silently skip
    }
  }, [getText]);

  return (
    <Tooltip label={copied ? 'Copied!' : label}>
      <IconButton
        aria-label={label}
        icon={copied ? <CheckIcon color="green.400" /> : <CopyIcon />}
        size={size as any}
        variant={variant as any}
        onClick={handleCopy}
        transition="all 0.15s"
      />
    </Tooltip>
  );
}

// ─── MessageBubble ────────────────────────────────────────────────────────────

function MessageBubble({
  message,
  isStreaming,
  mutedColor,
}: {
  message: ChatMessage;
  isStreaming?: boolean;
  mutedColor: string;
}) {
  const isUser = message.role === 'user';

  // User bubble: gray background, high-contrast text
  const userBg        = useColorModeValue('gray.100', 'gray.700');
  const userBorder    = useColorModeValue('gray.300',  'gray.600');
  const userText      = useColorModeValue('gray.900',  'gray.50');

  // Assistant bubble: white/near-white, black text — maximum contrast
  const aiBg          = useColorModeValue('white',     'gray.800');
  const aiBorder      = useColorModeValue('gray.200',  'gray.600');
  const aiText        = useColorModeValue('gray.900',  'white');
  const aiCodeBg      = useColorModeValue('gray.100',  'gray.700');
  const aiTableBorder = useColorModeValue('gray.300',  'gray.500');
  const aiThBg        = useColorModeValue('gray.100',  'gray.700');

  return (
    <Box position="relative" role="group" flexShrink={0} w="full">
      {/* Role label */}
      <HStack mb={1.5} spacing={2}>
        <Text fontSize="xs" fontWeight="semibold" color={isUser ? 'gray.500' : 'purple.500'}>
          {isUser ? 'You' : '✨ Assistant'}
        </Text>
        {isStreaming && !isUser && (
          <Spinner size="xs" color="purple.400" />
        )}
        <Box flex={1} />
        {message.content.length > 0 && (
          <Box opacity={0} _groupHover={{ opacity: 1 }} transition="opacity 0.15s">
            <CopyButton
              getText={() => message.content}
              label="Copy message"
            />
          </Box>
        )}
      </HStack>

      {/* Content */}
      <Box
        w="full"
        bg={isUser ? userBg : aiBg}
        borderWidth="1px"
        borderColor={isUser ? userBorder : aiBorder}
        borderRadius="lg"
        px={4}
        py={3}
        fontSize="sm"
        lineHeight={1.7}
        color={isUser ? userText : aiText}
        sx={isUser ? {} : {
          'h1, h2, h3': { fontWeight: 'bold', mt: 4, mb: 2, fontSize: 'md' },
          'ul, ol': { pl: 5, mt: 1 },
          'li': { mb: 1.5 },
          'strong': { fontWeight: 'semibold' },
          'table': { width: '100%', borderCollapse: 'collapse', fontSize: 'xs', my: 3 },
          'th, td': { border: '1px solid', borderColor: aiTableBorder, px: 2, py: 1 },
          'th': { bg: aiThBg, fontWeight: 'semibold' },
          'p': { mb: 3 },
          'p:last-child': { mb: 0 },
          'code': { bg: aiCodeBg, px: 1.5, py: 0.5, borderRadius: 'sm', fontSize: 'xs', fontFamily: 'mono' },
          'pre': { bg: aiCodeBg, p: 3, borderRadius: 'md', overflowX: 'auto', my: 2 },
          'pre code': { bg: 'transparent', p: 0 },
          'blockquote': { borderLeftWidth: '3px', borderLeftColor: 'purple.300', pl: 3, my: 2, color: mutedColor },
        }}
      >
        {isUser ? (
          <Text whiteSpace="pre-wrap" fontSize="sm" color={userText}>{message.content}</Text>
        ) : message.content.length > 0 ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {message.content}
          </ReactMarkdown>
        ) : (
          <Text fontSize="sm" color={mutedColor} fontStyle="italic">
            {isStreaming ? 'Generating...' : ''}
          </Text>
        )}
      </Box>
    </Box>
  );
}

// ─── History sub-components ───────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function HistoryList({
  items,
  loading,
  total,
  page,
  onSelect,
  onPageChange,
  borderColor,
  mutedColor,
  bg,
}: {
  items: HistoryConversation[];
  loading: boolean;
  total: number;
  page: number;
  onSelect: (id: string) => void;
  onPageChange: (p: number) => void;
  borderColor: string;
  mutedColor: string;
  bg: string;
}) {
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const pageSize = 20;
  const totalPages = Math.ceil(total / pageSize);

  if (loading) {
    return (
      <VStack py={8} spacing={2}>
        <Spinner color="purple.400" />
        <Text fontSize="sm" color={mutedColor}>Loading history...</Text>
      </VStack>
    );
  }

  if (items.length === 0) {
    return (
      <VStack py={10} spacing={2} color={mutedColor}>
        <TimeIcon boxSize={6} />
        <Text fontSize="sm">No conversations yet.</Text>
        <Text fontSize="xs">Run an analysis to start building your history.</Text>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={0}>
      {items.map(conv => {
        const tokens = conv.usageLogs[0];
        return (
          <Box
            key={conv.id}
            px={4}
            py={3}
            borderBottomWidth="1px"
            borderColor={borderColor}
            cursor="pointer"
            _hover={{ bg: hoverBg }}
            onClick={() => onSelect(conv.id)}
          >
            <HStack justify="space-between" mb={1}>
              <Badge colorScheme="purple" fontSize="xs" variant="subtle">
                {conv.dateRange ?? conv.groupBy}
              </Badge>
              <Text fontSize="xs" color={mutedColor}>{timeAgo(conv.createdAt)}</Text>
            </HStack>
            <Text fontSize="sm" fontWeight="medium" noOfLines={2} mb={1}>
              {conv.title ?? '(no title)'}
            </Text>
            <HStack spacing={3} flexWrap="wrap">
              <Text fontSize="xs" color={mutedColor}>{conv.agentId}</Text>
              <Text fontSize="xs" color={mutedColor}>·</Text>
              <Text fontSize="xs" color={mutedColor}>{conv.model.split('/').pop()}</Text>
              {tokens && (
                <>
                  <Text fontSize="xs" color={mutedColor}>·</Text>
                  <Text fontSize="xs" color={mutedColor}>
                    {tokens.inputTokens + tokens.outputTokens} tokens
                  </Text>
                </>
              )}
            </HStack>
          </Box>
        );
      })}

      {totalPages > 1 && (
        <HStack justify="center" py={3} spacing={2}>
          <Button size="xs" variant="ghost" isDisabled={page <= 1} onClick={() => onPageChange(page - 1)}>←</Button>
          <Text fontSize="xs" color={mutedColor}>{page} / {totalPages}</Text>
          <Button size="xs" variant="ghost" isDisabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>→</Button>
        </HStack>
      )}
    </VStack>
  );
}

function HistoryDetailView({
  detail,
  loading,
  onBack,
  onContinue,
  mutedColor,
  borderColor,
  bg,
}: {
  detail: HistoryConversationDetail | null;
  loading: boolean;
  onBack: () => void;
  onContinue: () => void;
  mutedColor: string;
  borderColor: string;
  bg: string;
}) {
  if (loading || !detail) {
    return (
      <VStack py={8} spacing={2}>
        <Spinner color="purple.400" />
        <Text fontSize="sm" color={mutedColor}>Loading conversation...</Text>
      </VStack>
    );
  }

  const totalTokens = detail.usageLogs.reduce(
    (acc, l) => ({ i: acc.i + l.inputTokens, o: acc.o + l.outputTokens }),
    { i: 0, o: 0 },
  );

  return (
    <VStack align="stretch" spacing={0} h="full">
      {/* Detail header */}
      <Box px={4} py={3} borderBottomWidth="1px" borderColor={borderColor}>
        <Button size="xs" variant="ghost" leftIcon={<ArrowBackIcon />} onClick={onBack} mb={2} pl={0}>
          Back to history
        </Button>
        <HStack flexWrap="wrap" spacing={2}>
          {detail.dateRange && <Badge colorScheme="purple">{detail.dateRange}</Badge>}
          <Badge colorScheme="gray">{detail.agentId}</Badge>
          <Badge colorScheme="gray" variant="outline">{detail.model.split('/').pop()}</Badge>
        </HStack>
        <HStack mt={1} spacing={3}>
          <Text fontSize="xs" color={mutedColor}>{new Date(detail.createdAt).toLocaleString()}</Text>
          {totalTokens.i > 0 && (
            <Text fontSize="xs" color={mutedColor}>
              In: {totalTokens.i} · Out: {totalTokens.o}
            </Text>
          )}
        </HStack>
      </Box>

      {/* Messages */}
      <VStack align="stretch" spacing={3} flex={1} overflowY="auto" px={4} py={3}>
        {detail.messages.map(m => (
          <MessageBubble
            key={m.id}
            message={{ id: m.id, role: m.role as 'user' | 'assistant', content: m.content, createdAt: new Date(m.createdAt) }}
            mutedColor={mutedColor}
          />
        ))}
      </VStack>

      {/* Continue button */}
      <Box px={4} py={3} borderTopWidth="1px" borderColor={borderColor}>
        <Alert status="info" borderRadius="md" mb={3} fontSize="xs" py={2}>
          <AlertIcon boxSize={3} />
          <AlertDescription>
            Data may have changed since this analysis. Continuing will use the current table as context.
          </AlertDescription>
        </Alert>
        <Button colorScheme="purple" size="sm" w="full" leftIcon={<span>↩</span>} onClick={onContinue}>
          Continue this conversation
        </Button>
      </Box>
    </VStack>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

function formatConversation(messages: ChatMessage[]): string {
  return messages
    .map(m => `${m.role === 'user' ? 'User' : 'Assistant'}:\n${m.content}`)
    .join('\n\n---\n\n');
}

function buildMarkdownReport(
  messages: ChatMessage[],
  context: string,
  prompt: string,
): string {
  const date = new Date().toLocaleString();
  const lines: string[] = [
    `# AI Analysis Report`,
    ``,
    `_Generated: ${date}_`,
    ``,
    `## Context`,
    ``,
    context,
    ``,
    `## Prompt`,
    ``,
    prompt,
    ``,
  ];

  for (const msg of messages) {
    if (msg.role === 'user') {
      lines.push(`## User`, ``, msg.content, ``);
    } else {
      lines.push(`## Analysis`, ``, msg.content, ``);
    }
  }

  return lines.join('\n');
}

// Scans upward from targetY to find a row of near-white pixels — safe to slice there
function findSafeBreakY(canvas: HTMLCanvasElement, targetY: number): number {
  const ctx = canvas.getContext('2d');
  if (!ctx) return targetY;
  const maxSearch = Math.min(80, targetY);
  for (let dy = 0; dy <= maxSearch; dy++) {
    const y = Math.floor(targetY - dy);
    if (y <= 0) return y;
    const row = ctx.getImageData(0, y, canvas.width, 1).data;
    let isLight = true;
    for (let x = 0; x < row.length; x += 16) {
      if (row[x] < 225 || row[x + 1] < 225 || row[x + 2] < 225) { isLight = false; break; }
    }
    if (isLight) return y;
  }
  return Math.floor(targetY);
}

async function downloadPdf(filename: string, messages: ChatMessage[], context: string, prompt: string): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const html2canvas = (await import('html2canvas')).default;

  const date = new Date().toLocaleString();

  // Escape first, then apply markdown transforms on the escaped text — none
  // of the patterns below match &, <, or >, so this is safe to compose.
  // Without this, an AI response that echoes attacker-controlled text (e.g.
  // a Plaid merchant name an attacker named "<img src=x onerror=...>")
  // executes when this HTML is assigned via container.innerHTML below.
  const mdToHtml = (md: string) => escapeHtml(md)
    .replace(/^### (.+)$/gm, '<h3 style="font-size:13px;margin:10px 0 4px;color:#2d3748">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 style="font-size:15px;margin:14px 0 5px;color:#1a202c">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 style="font-size:17px;margin:18px 0 7px">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^[-*] (.+)$/gm, '<li style="margin:2px 0">$1</li>')
    .replace(/(<li[^>]*>.+<\/li>\n?)+/g, '<ul style="margin:6px 0;padding-left:18px">$&</ul>')
    .replace(/`([^`]+)`/g, '<code style="background:#f1f5f9;padding:1px 4px;border-radius:3px;font-size:11px">$1</code>')
    .replace(/\n\n/g, '</p><p style="margin:6px 0">')
    .replace(/^(?!<[hpuoli])(.+)$/gm, '<p style="margin:6px 0">$1</p>');

  let html = `
    <h1 style="font-size:19px;border-bottom:2px solid #4a5568;padding-bottom:7px;margin-bottom:14px">AI Analysis Report</h1>
    <p style="color:#718096;font-size:11px;margin-bottom:18px">Generated: ${date}</p>
    <h2 style="font-size:14px;color:#4a5568;margin-bottom:5px">Context</h2>
    <pre style="background:#f7fafc;border:1px solid #e2e8f0;padding:9px;font-size:10px;white-space:pre-wrap;border-radius:4px;margin-bottom:14px">${escapeHtml(context)}</pre>
    <h2 style="font-size:14px;color:#4a5568;margin-bottom:5px">Prompt</h2>
    <pre style="background:#f7fafc;border:1px solid #e2e8f0;padding:9px;font-size:10px;white-space:pre-wrap;border-radius:4px;margin-bottom:14px">${escapeHtml(prompt)}</pre>
  `;

  for (const msg of messages) {
    if (msg.role === 'user') {
      html += `<h2 style="font-size:14px;color:#3182ce;margin:14px 0 5px">User</h2><div style="background:#ebf8ff;padding:9px;border-radius:4px;margin-bottom:10px">${escapeHtml(msg.content)}</div>`;
    } else {
      html += `<h2 style="font-size:14px;color:#276749;margin:14px 0 5px">Analysis</h2><div style="margin-bottom:10px">${mdToHtml(msg.content)}</div>`;
    }
  }

  const container = document.createElement('div');
  container.style.cssText = 'position:absolute;left:-9999px;top:0;width:750px;padding:32px;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;font-size:13px;color:#1a202c;line-height:1.65;background:#fff;';
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, { scale: 1.5, useCORS: true, backgroundColor: '#ffffff', logging: false });

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const scale = pageW / canvas.width;
    const pageHeightPx = Math.floor(pageH / scale);

    let currentY = 0;
    while (currentY < canvas.height) {
      const targetBreak = Math.min(currentY + pageHeightPx, canvas.height);
      const safeBreak = targetBreak < canvas.height ? findSafeBreakY(canvas, targetBreak) : canvas.height;
      const sliceH = safeBreak - currentY;

      const slice = document.createElement('canvas');
      slice.width = canvas.width;
      slice.height = sliceH;
      const ctx2 = slice.getContext('2d')!;
      ctx2.fillStyle = '#ffffff';
      ctx2.fillRect(0, 0, slice.width, slice.height);
      ctx2.drawImage(canvas, 0, currentY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

      if (currentY > 0) pdf.addPage();
      pdf.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, pageW, sliceH * scale);

      currentY = safeBreak;
    }

    pdf.save(filename);
  } finally {
    document.body.removeChild(container);
  }
}

function downloadMarkdown(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function handlePrint(context: string, prompt: string, responseHtml: string) {
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>AI Analysis — ${new Date().toLocaleDateString()}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 820px; margin: 2rem auto; color: #1a202c; line-height: 1.6; }
    h1 { font-size: 1.5rem; border-bottom: 2px solid #4a5568; padding-bottom: 0.5rem; }
    h2 { font-size: 1.1rem; color: #4a5568; margin-top: 2rem; }
    pre { background: #f7fafc; border: 1px solid #e2e8f0; padding: 1rem; white-space: pre-wrap; font-size: 0.85rem; border-radius: 4px; }
    table { border-collapse: collapse; width: 100%; font-size: 0.85rem; }
    th, td { border: 1px solid #e2e8f0; padding: 0.4rem 0.6rem; }
    th { background: #f7fafc; }
    strong { color: #2d3748; }
    @media print { button { display: none !important; } }
  </style>
</head>
<body>
  <h1>AI Analysis — ${new Date().toLocaleString()}</h1>
  <h2>Context Data</h2>
  <pre>${escapeHtml(context)}</pre>
  <h2>Prompt Used</h2>
  <pre>${escapeHtml(prompt)}</pre>
  <h2>Analysis</h2>
  ${responseHtml}
  <br/><button onclick="window.print()" style="padding:0.5rem 1rem;cursor:pointer;">Print</button>
</body>
</html>`);
  win.document.close();
}

/**
 * Escape text that is about to be interpolated into an HTML string.
 *
 * The AI response is attacker-influenceable — it can echo back Plaid
 * merchant/transaction text a counterparty controls — and the export paths
 * below feed it to innerHTML and document.write(). Order matters: & first,
 * otherwise the entities produced by the later replacements get
 * double-escaped.
 */
export function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function markdownToHtml(md: string): string {
  // Escape first, then apply markdown transforms on the escaped text —
  // none of the patterns below match &, < or >, so this is safe to
  // compose. Feeds handlePrint()'s document.write().
  return escapeHtml(md)
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.+<\/li>\n?)+/g, '<ul>$&</ul>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/^(?!<[hou])(.+)$/gm, '<p>$1</p>');
}


// ─── Component ────────────────────────────────────────────────────────────────

export const AiAnalysisPanel: React.FC<AiAnalysisPanelProps> = ({
  isOpen,
  onClose,
  matrixData,
  groupBy,
  period,
  viewMode,
  grouping,
}) => {
  const {
    analysisState,
    messages,
    error,
    tokenUsage,
    config,
    configError,
    configLoading,
    models,
    selectedAgent,
    selectedModel,
    prompt,
    setSelectedAgent,
    setSelectedModel,
    setPrompt,
    resetPrompt,
    analyze,
    sendFollowUp,
    analyzePeriod,
    stop,
    reset,
    loadConfig,
    history,
    historyTotal,
    historyPage,
    historyLoading,
    historyDetail,
    historyDetailLoading,
    loadHistory,
    loadHistoryDetail,
    clearHistoryDetail,
    resumeConversation,
  } = useAiAnalysis();

  const { isOpen: contextOpen, onToggle: toggleContext } = useDisclosure();
  const [activeTab, setActiveTab] = useState(0);
  const [followUpInput, setFollowUpInput] = useState('');

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const codeBg = useColorModeValue('gray.50', 'gray.900');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const usePeriodFlow = Boolean(period && grouping && viewMode === 'pivot');

  // Load config when drawer opens
  useEffect(() => {
    if (isOpen) loadConfig();
  }, [isOpen, loadConfig]);

  // Load history when switching to history tab
  useEffect(() => {
    if (activeTab === 1 && isOpen) {
      loadHistory(groupBy);
    }
  }, [activeTab, isOpen, groupBy, loadHistory]);

  // Close without resetting state — conversation is preserved across open/close
  const handleClose = useCallback(() => {
    stop();
    onClose();
  }, [stop, onClose]);

  const handleAnalyze = useCallback(() => {
    if (usePeriodFlow && period && grouping) {
      analyzePeriod(period, grouping);
    } else {
      analyze(matrixData, groupBy);
    }
  }, [usePeriodFlow, period, grouping, analyzePeriod, analyze, matrixData, groupBy]);

  const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant');

  const contextText = usePeriodFlow && period
    ? `Period: ${period.start} to ${period.end} (${grouping})`
    : `Table: ${matrixData.rows.length} months × ${matrixData.columns.length} ${groupBy === 'category' ? 'categories' : 'merchants'}`;

  const handlePrintClick = useCallback(() => {
    handlePrint(contextText, prompt, markdownToHtml(lastAssistantMessage?.content ?? ''));
  }, [contextText, prompt, lastAssistantMessage]);

  const handleDownloadMd = useCallback(() => {
    const md = buildMarkdownReport(messages, contextText, prompt);
    const date = new Date().toISOString().slice(0, 10);
    downloadMarkdown(`ai-analysis-${date}.md`, md);
  }, [messages, contextText, prompt]);

  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const handleDownloadPdf = useCallback(async () => {
    setIsExportingPdf(true);
    try {
      const date = new Date().toISOString().slice(0, 10);
      await downloadPdf(`ai-analysis-${date}.pdf`, messages, contextText, prompt);
    } finally {
      setIsExportingPdf(false);
    }
  }, [messages, contextText, prompt]);

  const handleSendFollowUp = useCallback(() => {
    const text = followUpInput.trim();
    if (!text) return;
    setFollowUpInput('');
    sendFollowUp(text, matrixData, groupBy);
  }, [followUpInput, sendFollowUp, matrixData, groupBy]);

  const isRunning = analysisState === 'streaming' || analysisState === 'preparing';
  const hasMessages = messages.length > 0;
  const hasResponse = lastAssistantMessage ? lastAssistantMessage.content.length > 0 : false;
  const promptTokens = estimateTokens(prompt);
  const contextTokens = estimateTokens(matrixData.columns.join('') + matrixData.rows.map(r => r.month).join(''));
  const showPrompt = !hasMessages && (analysisState === 'idle' || analysisState === 'error' || analysisState === 'stopped');
  const showFollowUp = hasMessages && !isRunning;

  const contextDescription = usePeriodFlow && period
    ? `${period.start} to ${period.end} (${grouping?.replace('-', ' / ')})`
    : `${matrixData.rows.length} months × ${matrixData.columns.length} ${groupBy === 'category' ? 'categories' : 'merchants'}`;

  return (
    <Drawer isOpen={isOpen} placement="right" size="lg" onClose={handleClose}>
      <DrawerOverlay backdropFilter="blur(2px)" />
      <DrawerContent bg={bg} maxW={{ base: '100%', md: '42%' }}>

        {/* ─── Header ─────────────────────────────────────────────── */}
        <DrawerHeader borderBottomWidth="1px" borderColor={borderColor} pb={3}>
          <HStack justify="space-between" align="start">
            <VStack align="start" spacing={1}>
              <Text fontWeight="bold" fontSize="md">✨ AI Analysis</Text>
              <Text fontSize="xs" color={mutedColor}>{contextDescription}</Text>
              {usePeriodFlow && (
                <Text fontSize="xs" color="purple.500" fontWeight="medium">
                  Context Source: Period Transactions
                </Text>
              )}
            </VStack>
            <HStack spacing={1}>
              {/* Copy entire conversation */}
              {hasMessages && (
                <CopyButton
                  getText={() => formatConversation(messages)}
                  label="Copy conversation"
                  size="sm"
                  variant="ghost"
                />
              )}
              {/* Download menu */}
              {hasResponse && !isRunning && (
                <Menu placement="bottom-end">
                  <Tooltip label="Download">
                    <MenuButton
                      as={IconButton}
                      aria-label="Download"
                      icon={isExportingPdf ? <Spinner size="xs" /> : <DownloadIcon />}
                      size="sm"
                      variant="ghost"
                      isDisabled={isExportingPdf}
                    />
                  </Tooltip>
                  <MenuList minW="160px" fontSize="sm">
                    <MenuItem onClick={handleDownloadMd}>Markdown (.md)</MenuItem>
                    <MenuItem onClick={handleDownloadPdf}>PDF (.pdf)</MenuItem>
                  </MenuList>
                </Menu>
              )}
              <DrawerCloseButton position="relative" top={0} right={0} />
            </HStack>
          </HStack>

          {/* Agent + Model selectors */}
          {config && (
            <HStack mt={3} spacing={2}>
              <Select
                size="sm"
                value={selectedAgent}
                onChange={e => setSelectedAgent(e.target.value)}
                isDisabled={isRunning}
                flex={1}
              >
                {config.agents.map(a => (
                  <option key={a.id} value={a.id}>{a.label}</option>
                ))}
              </Select>
              <Select
                size="sm"
                value={selectedModel}
                onChange={e => setSelectedModel(e.target.value)}
                isDisabled={isRunning}
                flex={1}
              >
                {models.map(m => (
                  <option key={m} value={m}>{m.split('/').pop()}</option>
                ))}
              </Select>
            </HStack>
          )}
        </DrawerHeader>

        {/* Config Error Alert */}
        {configError && (
          <Box px={4} pt={3}>
            <Alert status="error" borderRadius="md" size="sm">
              <AlertIcon />
              <Box flex="1">
                <AlertTitle fontSize="sm">Configuration Error</AlertTitle>
                <AlertDescription fontSize="xs">{configError}</AlertDescription>
              </Box>
              <Button
                size="xs"
                colorScheme="red"
                variant="outline"
                onClick={() => loadConfig(true)}
                isLoading={configLoading}
                ml={2}
              >
                Retry
              </Button>
            </Alert>
          </Box>
        )}

        {configLoading && !config && (
          <Box px={4} pt={3}>
            <Alert status="info" borderRadius="md" size="sm">
              <Spinner size="sm" mr={2} />
              <AlertDescription fontSize="xs">Loading AI configuration...</AlertDescription>
            </Alert>
          </Box>
        )}

        {/* ─── Tabs ────────────────────────────────────────────────── */}
        <Tabs
          index={activeTab}
          onChange={setActiveTab}
          display="flex"
          flexDirection="column"
          flex={1}
          minH={0}
        >
          <TabList px={4} borderBottomWidth="1px" borderColor={borderColor}>
            <Tab fontSize="sm" _selected={{ color: 'purple.500', borderColor: 'purple.500' }}>
              Chat
            </Tab>
            <Tab fontSize="sm" _selected={{ color: 'purple.500', borderColor: 'purple.500' }}>
              History{' '}
              {historyTotal > 0 && (
                <Badge ml={1} colorScheme="purple" variant="subtle" fontSize="xs">{historyTotal}</Badge>
              )}
            </Tab>
          </TabList>

          <TabPanels flex={1} minH={0} display="flex" flexDirection="column">

            {/* ─── Chat Tab ──────────────────────────────────────────── */}
            <TabPanel p={0} flex={1} minH={0} overflowY="auto">
              <VStack align="stretch" spacing={4} px={4} py={3}>

                {/* Context Section */}
                <Box>
                  <HStack justify="space-between" mb={1}>
                    <Button size="xs" variant="ghost" onClick={toggleContext} color={mutedColor}>
                      {contextOpen ? '▼' : '▶'} Context (~{contextTokens + promptTokens} estimated tokens)
                    </Button>
                    <Tooltip label="Copy table data">
                      <IconButton
                        aria-label="Copy context"
                        icon={<CopyIcon />}
                        size="xs"
                        variant="ghost"
                        onClick={() => navigator.clipboard.writeText(JSON.stringify(matrixData, null, 2)).catch(() => {})}
                      />
                    </Tooltip>
                  </HStack>
                  <Collapse in={contextOpen}>
                    <Box
                      bg={codeBg}
                      borderWidth="1px"
                      borderColor={borderColor}
                      borderRadius="md"
                      p={2}
                      fontSize="xs"
                      fontFamily="mono"
                      maxH="160px"
                      overflowY="auto"
                    >
                      <Text>Columns: {matrixData.columns.slice(0, 10).join(', ')}{matrixData.columns.length > 10 ? ` ... +${matrixData.columns.length - 10}` : ''}</Text>
                      <Text mt={1}>Months: {matrixData.rows.map(r => r.month).join(', ')}</Text>
                    </Box>
                  </Collapse>
                </Box>

                <Divider />

                {/* Prompt Section */}
                {showPrompt && (
                  <Box>
                    <HStack justify="space-between" mb={1} flexWrap="wrap" gap={1}>
                      <HStack spacing={2} flexWrap="wrap">
                        <Text fontSize="sm" fontWeight="medium">Prompt</Text>
                        <Text fontSize="xs" color={mutedColor}>
                          Variables:{' '}
                          <Box as="code" bg={codeBg} px={1} borderRadius="sm">{'{{table}}'}</Box>
                          {' '}·{' '}
                          <Box as="code" bg={codeBg} px={1} borderRadius="sm">{'{{groupLabel}}'}</Box>
                        </Text>
                      </HStack>
                      <HStack spacing={1}>
                        <Badge colorScheme={promptTokens > 800 ? 'orange' : 'gray'} fontSize="xs">
                          ~{promptTokens} tokens
                        </Badge>
                        <Button size="xs" variant="ghost" onClick={resetPrompt} color={mutedColor}>
                          Reset
                        </Button>
                      </HStack>
                    </HStack>
                    <AutoResizeTextarea
                      value={prompt}
                      onChange={e => setPrompt((e.target as HTMLTextAreaElement).value)}
                      size="sm"
                      placeholder="Describe what analysis you want to perform..."
                      isDisabled={isRunning}
                      fontFamily="mono"
                      fontSize="xs"
                    />
                    <HStack mt={2} spacing={2} align="center">
                      <Button
                        colorScheme="purple"
                        size="sm"
                        onClick={handleAnalyze}
                        isDisabled={!config || configLoading}
                        isLoading={configLoading}
                      >
                        Send
                      </Button>
                      {promptTokens > 800 && (
                        <Text fontSize="xs" color="orange.500">Long prompt — may affect speed.</Text>
                      )}
                    </HStack>
                  </Box>
                )}

                {/* Error alert */}
                {analysisState === 'error' && error && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="sm">{error}</AlertDescription>
                  </Alert>
                )}

                {/* Messages list */}
                {hasMessages && (
                  <VStack align="stretch" spacing={3}>
                    {tokenUsage.output > 0 && (
                      <Text fontSize="xs" color={mutedColor} textAlign="right">
                        In: {tokenUsage.input} | Out: {tokenUsage.output} tokens
                      </Text>
                    )}
                    {messages.map((msg, idx) => {
                      const isLastAssistant = msg.role === 'assistant' && idx === messages.length - 1;
                      return (
                        <MessageBubble
                          key={msg.id}
                          message={msg}
                          isStreaming={isRunning && isLastAssistant}
                          mutedColor={mutedColor}
                        />
                      );
                    })}
                    {analysisState === 'stopped' && (
                      <Badge colorScheme="yellow" alignSelf="flex-start" fontSize="xs">Stopped</Badge>
                    )}
                  </VStack>
                )}

                {/* Follow-up input — shown after a completed/stopped response */}
                {showFollowUp && (
                  <Box>
                    <Divider mb={3} />
                    <AutoResizeTextarea
                      value={followUpInput}
                      onChange={e => setFollowUpInput((e.target as HTMLTextAreaElement).value)}
                      placeholder="Continue the conversation..."
                      size="sm"
                      isDisabled={isRunning}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendFollowUp();
                        }
                      }}
                    />
                    <HStack mt={2} justify="space-between" align="center" spacing={2}>
                      <Button
                        size="sm"
                        colorScheme="purple"
                        onClick={handleSendFollowUp}
                        isDisabled={!followUpInput.trim()}
                      >
                        Send
                      </Button>
                      <Text fontSize="xs" color={mutedColor}>Shift+Enter for newline</Text>
                    </HStack>
                  </Box>
                )}

              </VStack>
            </TabPanel>

            {/* ─── History Tab ───────────────────────────────────────── */}
            <TabPanel p={0} flex={1} minH={0} overflowY="auto">
              {historyDetail || historyDetailLoading ? (
                <HistoryDetailView
                  detail={historyDetail}
                  loading={historyDetailLoading}
                  onBack={clearHistoryDetail}
                  onContinue={() => {
                    if (historyDetail) {
                      resumeConversation(historyDetail);
                      setActiveTab(0);
                    }
                  }}
                  mutedColor={mutedColor}
                  borderColor={borderColor}
                  bg={bg}
                />
              ) : (
                <HistoryList
                  items={history}
                  loading={historyLoading}
                  total={historyTotal}
                  page={historyPage}
                  onSelect={loadHistoryDetail}
                  onPageChange={p => loadHistory(groupBy, p)}
                  borderColor={borderColor}
                  mutedColor={mutedColor}
                  bg={bg}
                />
              )}
            </TabPanel>

          </TabPanels>
        </Tabs>

        {/* ─── Footer Controls ─────────────────────────────────────── */}
        <DrawerFooter borderTopWidth="1px" borderColor={borderColor} gap={2}>
          {activeTab === 0 && (
            <>
              {isRunning && (
                <Button colorScheme="red" variant="outline" size="sm" onClick={stop}>
                  Stop
                </Button>
              )}

              {hasMessages && !isRunning && (
                <Button variant="outline" size="sm" onClick={handleAnalyze} leftIcon={<span>↺</span>}>
                  Regenerate
                </Button>
              )}

              {hasResponse && !isRunning && (
                <CopyButton
                  getText={() => lastAssistantMessage?.content ?? ''}
                  label="Copy response"
                  size="sm"
                  variant="outline"
                />
              )}

              {hasMessages && !isRunning && (
                <Button variant="ghost" size="sm" onClick={reset}>
                  New query
                </Button>
              )}
            </>
          )}

          <Button variant="ghost" size="sm" onClick={handleClose} ml="auto">
            Close
          </Button>
        </DrawerFooter>

      </DrawerContent>
    </Drawer>
  );
};
